import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { X, CreditCard, Bitcoin, Wallet, Copy, Check } from 'lucide-react';
import axios from 'axios';

const DepositModal = ({ onClose }) => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const { tt } = useLanguage();
  const [amount, setAmount] = useState('');
  const [depositMethods, setDepositMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedLine, setCopiedLine] = useState(null); // Track which line was copied

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchDepositMethods();
  }, []);

  const fetchDepositMethods = async () => {
    try {
      const response = await axios.get(`${API}/deposit-methods`);
      const enabledMethods = response.data.filter(method => method.enabled);
      setDepositMethods(enabledMethods);
      if (enabledMethods.length > 0) {
        setSelectedMethod(enabledMethods[0].id);
      }
    } catch (error) {
      console.error('Error fetching deposit methods:', error);
      // Fallback to default methods
      const defaultMethods = [
        { id: 'visa', type: 'visa_mastercard', title: 'VISA/MasterCard', description: 'Банковские карты Visa, MasterCard', details: 'Свяжитесь с поддержкой для получения реквизитов', enabled: true },
        { id: 'card', type: 'bank_card', title: 'Карта', description: 'Дебетовые и кредитные карты', details: 'Свяжитесь с поддержкой для получения реквизитов', enabled: true },
        { id: 'crypto', type: 'crypto', title: 'Криптовалюта', description: 'Bitcoin, Ethereum, USDT', details: 'Свяжитесь с поддержкой для получения реквизитов', enabled: true }
      ];
      setDepositMethods(defaultMethods);
      setSelectedMethod(defaultMethods[0].id);
    } finally {
      setLoading(false);
    }
  };

  const getMethodIcon = (type) => {
    switch (type) {
      case 'exchange':
        return Wallet;
      case 'visa_mastercard':
      case 'bank_card':
        return CreditCard;
      case 'crypto':
        return Bitcoin;
      default:
        return Wallet;
    }
  };
  
  const handleDeposit = () => {
    if (!amount || parseFloat(amount) < 10) {
      alert(tt('Минимальная сумма пополнения: $10'));
      return;
    }
    
    if (!selectedMethod) {
      alert(tt('Выберите способ пополнения'));
      return;
    }
    
    const method = depositMethods.find(m => m.id === selectedMethod);
    
    if (method && method.type === 'exchange' && method.redirect_url) {
      // Для метода "Обмен" делаем редирект на внешний сайт
      const url = method.redirect_url.replace('{amount}', amount);
      window.open(url, '_blank');
      onClose();
    } else {
      // Для остальных методов показываем детали
      setShowDetails(true);
    }
  };

  const copyToClipboard = async (text, lineIndex = null) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setCopiedLine(lineIndex);
      setTimeout(() => {
        setCopied(false);
        setCopiedLine(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setCopiedLine(lineIndex);
      setTimeout(() => {
        setCopied(false);
        setCopiedLine(null);
      }, 2000);
    }
  };

  const quickAmounts = [50, 100, 250, 500, 1000, 2500];

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center z-[999999999] p-4">
      <div className="bg-white rounded-lg p-4 w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{tt('Пополнение счета')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Amount Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Сумма пополнения (USD)
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                const value = e.target.value;
                // Разрешаем только цифры и точку, пустое поле тоже разрешаем
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  setAmount(value);
                }
              }}
              placeholder={tt('Введите сумму')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white placeholder-gray-400"
            />
          </div>

          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Способ пополнения
            </label>
            {loading ? (
              <div className="text-center py-2 text-gray-500">
                Загрузка способов пополнения...
              </div>
            ) : (
              <div className="space-y-1">
                {depositMethods.map((method) => {
                  const IconComponent = getMethodIcon(method.type);
                  return (
                    <label key={method.id} className="flex items-center p-2 border rounded-md cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedMethod === method.id}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-2 flex items-center">
                        <IconComponent className="w-4 h-4 text-gray-600 mr-2" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{method.title}</div>
                          <div className="text-xs text-gray-500">{method.description}</div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action Buttons - Показываем детали только после нажатия "Пополнить" */}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              onClick={handleDeposit}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              disabled={!amount || parseFloat(amount) < 10 || !selectedMethod}
            >
              Пополнить
            </button>
          </div>
        </div>
      </div>
      
      {/* Details Modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[999999999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{tt('Реквизиты для пополнения')}</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-700">{tt('Способ:')}</span>
                <span className="ml-2 text-sm text-gray-900">
                  {depositMethods.find(m => m.id === selectedMethod)?.title}
                </span>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700">{tt('Сумма:')}</span>
                <span className="ml-2 text-sm text-gray-900">${amount}</span>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Данные для перевода:
                  </label>
                  <button
                    onClick={() => {
                      const method = depositMethods.find(m => m.id === selectedMethod);
                      if (method) {
                        copyToClipboard(method.details);
                      }
                    }}
                    className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    title={tt('Копировать все реквизиты')}
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    <span>{copied ? 'Скопировано!' : 'Копировать всё'}</span>
                  </button>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border space-y-2">
                  {(() => {
                    const method = depositMethods.find(m => m.id === selectedMethod);
                    if (!method) {
                      return <p className="text-sm text-gray-500">{tt('Данные не найдены')}</p>;
                    }
                    
                    // Разбиваем на строки и создаём отдельные элементы для копирования
                    const lines = method.details.split('\n').filter(line => line.trim());
                    
                    return lines.map((line, index) => {
                      // Извлекаем значение после ":" для копирования
                      const colonIndex = line.indexOf(':');
                      const hasColon = colonIndex !== -1;
                      const label = hasColon ? line.substring(0, colonIndex + 1) : '';
                      const value = hasColon ? line.substring(colonIndex + 1).trim() : line.trim();
                      
                      return (
                        <div key={index} className="flex items-center justify-between bg-white rounded p-2 border border-gray-200 group hover:border-blue-300 transition-colors">
                          <div className="flex-1 min-w-0 mr-2">
                            {hasColon ? (
                              <span className="text-sm">
                                <span className="text-gray-500">{label}</span>
                                <span className="text-gray-900 font-medium ml-1 break-all">{value}</span>
                              </span>
                            ) : (
                              <span className="text-sm text-gray-900 break-all">{line}</span>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(value || line, index);
                            }}
                            className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                              copiedLine === index 
                                ? 'text-green-600 bg-green-50' 
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title={copiedLine === index ? 'Скопировано!' : `Копировать: ${value || line}`}
                          >
                            {copiedLine === index ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  После совершения платежа обратитесь в службу поддержки для подтверждения транзакции.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Закрыть
                </button>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    onClose();
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositModal;